import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Crown, ExternalLink, Loader2, RefreshCw, Phone } from 'lucide-react';
import { useSubscription } from '@/hooks/use-subscription';
import { TIERS, TierKey, FOUNDING_OFFER_EXPIRES } from '@/lib/subscription';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import FoundingMemberBadge from '@/components/FoundingMemberBadge';

export function SubscriptionCard() {
  const { subscribed, tier, subscriptionEnd, loading, startCheckout, openPortal, checkSubscription, isFoundingMember, foundingOfferActive } = useSubscription();
  const { toast } = useToast();
  const [actionLoading, setActionLoading] = useState<TierKey | null>(null);
  const [pendingTier, setPendingTier] = useState<TierKey | null>(null);
  const [mobile, setMobile] = useState('+61');

  const handleUpgrade = (tierKey: TierKey) => {
    setPendingTier(tierKey);
  };

  const handleConfirmUpgrade = async () => {
    if (!pendingTier) return;
    if (mobile.length < 8 || !/^\+\d{6,15}$/.test(mobile)) {
      toast({ title: 'Please enter a valid mobile number', description: 'Use international format e.g. +61400000000', variant: 'destructive' });
      return;
    }

    setActionLoading(pendingTier);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('user_pii').upsert({ user_id: user.id, mobile }, { onConflict: 'user_id' });
      }

      const t = TIERS[pendingTier];
      await startCheckout(t.price_id, t.mode);
    } catch {
      toast({ title: 'Failed to start checkout', variant: 'destructive' });
    } finally {
      setActionLoading(null);
      setPendingTier(null);
    }
  };

  const handleManage = async () => {
    setActionLoading('monthly');
    try {
      await openPortal();
    } catch {
      toast({ title: 'Failed to open subscription portal', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const activeTierLabel = tier ? TIERS[tier].name : 'Free';

  // Determine which tiers to show: hide founding_member if offer expired, hide lifetime if offer still active
  const visibleTiers = (Object.entries(TIERS) as [TierKey, typeof TIERS[TierKey]][]).filter(([key]) => {
    if (key === 'founding_member') return foundingOfferActive;
    if (key === 'lifetime') return !foundingOfferActive;
    return true;
  });

  const daysUntilExpiry = Math.max(0, Math.ceil((FOUNDING_OFFER_EXPIRES.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));

  return (
    <Card className={subscribed ? 'border-primary/40 bg-primary/5' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${subscribed ? 'bg-primary/20' : 'bg-primary/10'}`}>
            <Crown className={`h-5 w-5 ${subscribed ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-medium">Subscription</p>
              {isFoundingMember ? (
                <FoundingMemberBadge size="sm" />
              ) : (
                <Badge variant={subscribed ? 'default' : 'secondary'}>
                  {activeTierLabel}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {subscribed && isFoundingMember
                ? 'Founding Member — lifetime access'
                : subscribed && subscriptionEnd
                ? `Renews ${new Date(subscriptionEnd).toLocaleDateString('en-AU')}`
                : subscribed && tier === 'lifetime'
                ? 'Lifetime access — no renewal needed'
                : 'Upgrade to unlock live bank sync'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={checkSubscription}
            className="h-8 w-8"
            title="Refresh subscription status"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Founding member badge for subscribed founding members */}
        {subscribed && isFoundingMember && (
          <div className="mb-3">
            <FoundingMemberBadge size="lg" />
          </div>
        )}

        {subscribed ? (
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={handleManage}
            disabled={actionLoading !== null}
          >
            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            Manage Subscription
          </Button>
        ) : pendingTier ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Phone className="h-4 w-4 text-primary" />
                <p className="text-sm font-medium">Mobile Number Required</p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Pro includes live bank sync powered by Basiq. Your mobile number is required by Basiq to securely verify your identity when connecting your bank accounts. It will only be used for bank connection verification.
              </p>
              <Input
                type="tel"
                placeholder="+61400000000"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="mb-2"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setPendingTier(null)}
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  className="flex-1 gap-1.5"
                  onClick={handleConfirmUpgrade}
                  disabled={actionLoading !== null || mobile.length < 8}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
                  Continue to Checkout
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Founding Member banner when offer is active */}
            {foundingOfferActive && (
              <div className="relative overflow-hidden rounded-lg border border-amber-300/50 bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 dark:from-amber-950/40 dark:via-yellow-950/30 dark:to-amber-900/40 dark:border-amber-600/40 p-3">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="h-4 w-4 text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
                    </svg>
                    <p className="text-sm font-bold text-amber-800 dark:text-amber-300">Founding Member — $149 AUD</p>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400/60 text-amber-700 dark:text-amber-300">
                      {daysUntilExpiry} days left
                    </Badge>
                  </div>
                  <p className="text-xs text-amber-700/80 dark:text-amber-300/70 mb-2">
                    One-time payment for lifetime access. Launch cohort exclusive — this offer won't return.
                  </p>
                  <ul className="text-xs text-amber-800/70 dark:text-amber-300/60 space-y-0.5 mb-2">
                    {TIERS.founding_member.features.map(f => <li key={f}>✓ {f}</li>)}
                  </ul>
                  <Button
                    className="w-full gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-amber-950 border-0"
                    size="sm"
                    onClick={() => handleUpgrade('founding_member')}
                    disabled={actionLoading !== null}
                  >
                    {actionLoading === 'founding_member' ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
                      </svg>
                    )}
                    Become a Founding Member
                  </Button>
                </div>
              </div>
            )}

            {/* Regular tiers */}
            {visibleTiers.filter(([key]) => key !== 'founding_member').map(([key, t]) => (
              <div key={key} className="rounded-lg bg-muted/50 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium">{t.name} — {t.price} AUD{t.interval !== 'once' ? `/${t.interval}` : ''}</p>
                  {'badge' in t && t.badge && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{t.badge}</Badge>
                  )}
                </div>
                <ul className="text-xs text-muted-foreground space-y-1 mb-2">
                  {t.features.map(f => (
                    <li key={f}>✓ {f}</li>
                  ))}
                </ul>
                <Button
                  className="w-full gap-2"
                  variant={key === 'lifetime' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleUpgrade(key)}
                  disabled={actionLoading !== null}
                >
                  {actionLoading === key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
                  {t.interval === 'once' ? `Buy ${t.name}` : `Subscribe ${t.name}`}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
